import { call, put, select } from 'redux-saga/effects';
import { Wretcher } from 'wretch/dist/wretcher';
import { IRootState } from '../state';
import { Action } from './redux';

const DEFAULT_PAGE_SIZE = 20;
const STARTING_INDEX = 1;

export const INITIAL_INDEX = STARTING_INDEX - 1;

interface IPaginatedRequestParams {
  apiEndPoint: Wretcher;
  pageNumber: number;
  pageSize: number;
}

export const paginatedRequest = <OutPutType>({
  apiEndPoint,
  pageNumber,
  pageSize,
}: IPaginatedRequestParams) =>
  apiEndPoint
    .query(`page[number]=${pageNumber}&page[size]=${pageSize}`)
    .get()
    .json<OutPutType>();

interface IUpdateCurrentPageParams {
  currentPage: number;
  numberOfItems: number;
  pageSize: number;
  reset?: boolean;
}

export const updateCurrentPage = ({
  currentPage,
  numberOfItems,
  pageSize,
  reset,
}: IUpdateCurrentPageParams) => {
  if (reset) return STARTING_INDEX;

  if (numberOfItems < currentPage * pageSize) return null;

  return currentPage + 1;
};

interface IGetPaginatedDataParameters
  extends IPaginationParameters,
    IPaginatedAsynchronousCallParameters {}

interface IPaginationParameters {
  pageSelector: (state: IRootState) => number;
  totalSelector: (state: IRootState) => number | undefined;
  pageSize?: number;
}

interface IPaginatedAsynchronousCallParameters {
  apiCall: (pageNumber: number, pageSize?: number) => IterableIterator<any>;
  apiCallOptionalParameter?: any[];
  resetAction: (...parameter: any[]) => Action<string>;
  resetActionParameters?: any[];
  successAction: (response: any, ...parameter: any[]) => Action<string>;
  successActionParameters?: any[];
  reset?: boolean;
}

export function* getPaginatedData({
  pageSelector,
  totalSelector,
  apiCall,
  apiCallOptionalParameter = [],
  resetAction,
  resetActionParameters = [],
  successAction,
  successActionParameters = [],
  reset,
  pageSize = DEFAULT_PAGE_SIZE,
}: IGetPaginatedDataParameters) {
  // eslint-disable-next-line no-useless-catch
  try {
    const currentPage = yield select(pageSelector);
    const numberOfItems = yield select(totalSelector);
    const updatedCurrentPage = yield call(updateCurrentPage, {
      currentPage,
      numberOfItems,
      pageSize,
      reset,
    });

    if (!updatedCurrentPage) return;

    const response = yield call(
      apiCall,
      updatedCurrentPage,
      ...apiCallOptionalParameter
    );

    if (reset) yield put(resetAction(...resetActionParameters));

    yield put(successAction(response, ...successActionParameters));

    return response;
  } catch (e) {
    // TODO: see if still necessary once toaster shwing will be handled in handleAsyncStatusSaga
    throw e;
  }
}

export interface IPaginatedStoreResource {
  meta: IPaginationMetadata;
}

export interface IPaginationMetadata {
  currentPage: number;
  total?: number;
}
